/**
 * Gate 2 — Test Execution & Evidence Collection: shared step list and styles.
 *
 * Gate 2 is one route with four internal steps rather than four routes: the step
 * rail, the readiness bar and the scope selection are all one workflow whose
 * state (selected suites, declared builds, execution results) has to survive
 * moving between steps.
 */

import {
  PAGE_HEADER_SUBTITLE_SX,
  PAGE_HEADER_TITLE_SX,
  STICKY_PAGE_HEADER_OUTER_ROW_SX,
  STICKY_PAGE_HEADER_SX,
  STICKY_PAGE_HEADER_SUBTITLE_SX,
  STICKY_PAGE_HEADER_TEXT_COLUMN_SX,
} from "../../components/layout/stickyPageHeaderTokens";

export const GATE2_STEPS = [
  { id: "scope", code: "G2-E1", label: "Define Scope" },
  { id: "assign", code: "G2-E2", label: "Review & Execute" },
  { id: "review", code: "G2-E3", label: "Evidence" },
  { id: "decision", code: "G2-E4", label: "Decision" },
];

export const STEP_STATE_CHIP = {
  established: { label: "Completed", bgcolor: "#E8FCF3", color: "#00754E" },
  active: { label: "In Progress", bgcolor: "#EFF6FF", color: "#005BF0" },
  planned: { label: "Planned", bgcolor: "#E6E6E6", color: "#64748B" },
};

/** Step rail — Figma Flag Default / Success (“Completed”), 75×18. */
export const STEP_RAIL_COMPLETED_CHIP_SX = {
  width: 75,
  minWidth: 75,
  maxWidth: 75,
  height: 18,
  minHeight: 18,
  maxHeight: 18,
  boxSizing: "border-box",
  borderRadius: "12px",
  gap: "2px",
  px: 0,
  opacity: 1,
  bgcolor: "#E8FCF3",
  color: "#00754E",
  border: "1px solid #00754E",
  boxShadow: "none",
  fontFamily: "Lato, sans-serif",
  fontSize: 10,
  fontWeight: 600,
  lineHeight: "18px",
  letterSpacing: 0,
  flexShrink: 0,
  "& .MuiChip-label": {
    px: "8px",
    py: 0,
    lineHeight: "18px",
    paddingLeft: "8px",
    paddingRight: "8px",
  },
};

/** Step rail — Figma Flag Default / Info (“In Progress”), 76×18. */
export const STEP_RAIL_IN_PROGRESS_CHIP_SX = {
  width: 76,
  minWidth: 76,
  maxWidth: 76,
  height: 18,
  minHeight: 18,
  maxHeight: 18,
  boxSizing: "border-box",
  borderRadius: "12px",
  gap: "10px",
  px: 0,
  opacity: 1,
  bgcolor: "#EFF6FF",
  color: "#005BF0",
  border: "none",
  boxShadow: "none",
  fontFamily: "Lato, sans-serif",
  fontSize: 10,
  fontWeight: 600,
  lineHeight: "18px",
  letterSpacing: 0,
  flexShrink: 0,
  "& .MuiChip-label": {
    px: "8px",
    py: 0,
    lineHeight: "18px",
    paddingLeft: "8px",
    paddingRight: "8px",
  },
};

/** Step rail — Planned pill (Grey-20), 60×18. */
export const STEP_RAIL_PLANNED_CHIP_SX = {
  width: 60,
  minWidth: 60,
  maxWidth: 60,
  height: 18,
  minHeight: 18,
  maxHeight: 18,
  boxSizing: "border-box",
  borderRadius: "8px",
  px: 0,
  py: 0,
  opacity: 1,
  bgcolor: "#E6E6E6",
  color: "#64748B",
  border: "none",
  boxShadow: "none",
  fontFamily: "Lato, sans-serif",
  fontSize: 12,
  fontWeight: 700,
  lineHeight: "100%",
  letterSpacing: 0,
  flexShrink: 0,
  "& .MuiChip-label": {
    px: "8px",
    py: "2px",
    lineHeight: "100%",
    paddingTop: "2px",
    paddingBottom: "2px",
    paddingLeft: "8px",
    paddingRight: "8px",
  },
};

/** Step rail label — any row with state In Progress (active step). */
export const STEP_RAIL_LABEL_IN_PROGRESS_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#FFFFFF",
};

/** Step rail label — non-active rows (e.g. Review & Execute, Evidence, Decision). */
export const STEP_RAIL_LABEL_DEFAULT_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 700,
  lineHeight: "100%",
  letterSpacing: 0,
  color: "#383838",
};

/** Keyboard focus — shared across Gate 2 controls */
export const GATE2_FOCUS_RING_SX = {
  outline: "none",
  boxShadow: "0 0 0 2px #005BF0",
};

export const GATE2_FOCUS_RING_INSET_SX = {
  outline: "none",
  boxShadow: "inset 0 0 0 2px #005BF0",
};

export const GATE2_FOCUS_RING_ON_PRIMARY_BG_SX = {
  outline: "none",
  boxShadow: "0 0 0 2px #FFFFFF, 0 0 0 4px #005BF0",
};

export const GATE2_BTN_FOCUS_SX = {
  "&:focus": { outline: "none" },
  "&:focus-visible:not(:active)": GATE2_FOCUS_RING_SX,
};

export const GATE2_PRIMARY_BTN_FOCUS_SX = {
  "&:focus": { outline: "none" },
  "&:focus-visible:not(:active)": GATE2_FOCUS_RING_ON_PRIMARY_BG_SX,
};

export const GATE2_TOGGLE_FOCUS_SX = {
  "&:focus": { outline: "none" },
  "&:focus-visible": GATE2_FOCUS_RING_SX,
  "&.Mui-selected:focus-visible": GATE2_FOCUS_RING_ON_PRIMARY_BG_SX,
};

/** Step rail row — base button reset */
export const STEP_RAIL_ROW_BASE_SX = {
  width: "100%",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "10px",
  boxSizing: "border-box",
  minHeight: 48,
  pl: 2,
  pr: 1,
  py: "4px",
  m: 0,
  border: "none",
  borderLeft: "3px solid transparent",
  borderBottom: "none",
  bgcolor: "transparent",
  textAlign: "left",
  font: "inherit",
  color: "inherit",
  cursor: "default",
  "&:focus": { outline: "none" },
  "&:focus:not(:focus-visible)": { boxShadow: "none" },
};

/** Active (in progress) step — focusable, not navigable */
export const STEP_RAIL_ROW_ACTIVE_SX = {
  borderLeftColor: "#005BF0",
  bgcolor: "#005BF0",
  cursor: "default",
  "&:focus-visible": GATE2_FOCUS_RING_ON_PRIMARY_BG_SX,
};

/** Completed step — navigable */
export const STEP_RAIL_ROW_NAVIGABLE_SX = {
  cursor: "pointer",
  "&:hover": { bgcolor: "#F8FAFC" },
  "&:focus-visible": {
    ...GATE2_FOCUS_RING_INSET_SX,
    bgcolor: "#F0F6FF",
  },
};

/** Planned step — not in tab order */
export const STEP_RAIL_ROW_PLANNED_SX = {
  cursor: "default",
};

/** Main content header — title for whichever step is In Progress. */
export const GATE2_STEP_PAGE_TITLE_SX = PAGE_HEADER_TITLE_SX;

/** Main content header — subtitle under the active step title. */
export const GATE2_STEP_PAGE_SUBTITLE_SX = PAGE_HEADER_SUBTITLE_SX;

/** Sticky step header (Define Scope, Review & Execute) — 16px vertical padding inside the bar. */
export const GATE2_STICKY_STEP_HEADER_SX = STICKY_PAGE_HEADER_SX;

/** Back + title/subtitle column row. */
export const GATE2_STICKY_STEP_HEADER_TITLE_ROW_SX = STICKY_PAGE_HEADER_OUTER_ROW_SX;

export const GATE2_STICKY_STEP_HEADER_TEXT_COLUMN_SX = STICKY_PAGE_HEADER_TEXT_COLUMN_SX;

/** Subtitle in the same column as the title (aligns with first letter). */
export const GATE2_STICKY_STEP_HEADER_SUBTITLE_SX = STICKY_PAGE_HEADER_SUBTITLE_SX;

export const BTN_TEXT_SX = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 15,
  lineHeight: "24px",
  letterSpacing: 0,
  boxSizing: "border-box",
  "& .MuiTouchRipple-root": { display: "none" },
};

const BTN_SHADOW = "0px 1px 2px 0px #0000000A";
const BTN_INSET = "inset 0px 1px 4px 0px rgba(0, 0, 0, 0.5)";
const PRIMARY_BG =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const PRIMARY_BG_ACTIVE =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

/** Solid primary — same states as Gate 1 / Test Suite primary buttons. */
export const PRIMARY_BTN_SX = {
  ...BTN_TEXT_SX,
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  borderRadius: "4px",
  color: "#FFFFFF",
  bgcolor: "transparent",
  backgroundColor: "transparent",
  backgroundImage: PRIMARY_BG,
  border: "1px solid transparent",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "transparent",
    backgroundColor: "transparent",
    backgroundImage: PRIMARY_BG_HOVER,
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG} !important`,
    border: "2px solid #005FCC",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#FFFFFF",
    bgcolor: "transparent !important",
    backgroundColor: "transparent !important",
    backgroundImage: `${PRIMARY_BG_ACTIVE} !important`,
    border: "1px solid transparent",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    color: "#BBBBBB !important",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #E6E6E6 !important",
    boxShadow: BTN_SHADOW,
    opacity: "1 !important",
    cursor: "not-allowed",
  },
};

/** Grey secondary — filled #F5F5F5, Grey/60 label. */
export const OUTLINED_BTN_SX = {
  ...BTN_TEXT_SX,
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  borderRadius: "4px",
  border: "1px solid #BBBBBB",
  bgcolor: "#F5F5F5",
  backgroundColor: "#F5F5F5",
  backgroundImage: "none",
  color: "#555555",
  boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#EEEEEE",
    backgroundColor: "#EEEEEE",
    backgroundImage: "none",
    border: "1px solid #BBBBBB",
    color: "#555555",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    color: "#555555",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "2px solid #555555",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "2px solid #555555",
    color: "#555555",
  },
  "&:active:not(.Mui-disabled)": {
    color: "#555555",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #BBBBBB",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    color: "#BBBBBB !important",
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #E6E6E6 !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: "1 !important",
    cursor: "not-allowed",
  },
};

/** Figma Ghost Primary Medium — e.g. Quality Scorecard “Add Custom Test Case” */
export const GHOST_PRIMARY_MEDIUM_BTN_SX = {
  textTransform: "none",
  fontFamily: "Lato, sans-serif",
  fontWeight: 700,
  fontSize: 13,
  lineHeight: "24px",
  letterSpacing: 0,
  boxSizing: "border-box",
  height: 32,
  minHeight: 32,
  minWidth: 140,
  py: "4px",
  px: "16px",
  gap: "4px",
  borderRadius: "4px",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  border: "1px solid #005BF0",
  color: "#005BF0",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  "& .MuiTouchRipple-root": { display: "none" },
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #E6E6E6 !important",
    color: "#BBBBBB !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: "1 !important",
    cursor: "not-allowed",
  },
};

/** Ghost primary, 40px — scope and Gate 3 Back buttons. */
export const SCOPE_CANCEL_BTN_SX = {
  ...BTN_TEXT_SX,
  height: 40,
  minHeight: 40,
  px: "16px",
  py: "8px",
  borderRadius: "4px",
  border: "1px solid #005BF0",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  color: "#005BF0",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #E6E6E6 !important",
    color: "#BBBBBB !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: "1 !important",
    cursor: "not-allowed",
  },
};

/** Ghost primary, 32px — connection and row actions. */
export const ROW_BTN_SX = {
  ...BTN_TEXT_SX,
  fontSize: 13,
  lineHeight: "20px",
  height: 32,
  minHeight: 32,
  minWidth: 96,
  px: "12px",
  py: "6px",
  borderRadius: "4px",
  border: "1px solid #005BF0",
  color: "#005BF0",
  bgcolor: "#FFFFFF",
  backgroundColor: "#FFFFFF",
  backgroundImage: "none",
  boxShadow: BTN_SHADOW,
  whiteSpace: "nowrap",
  "&:hover:not(.Mui-disabled):not(:focus):not(:focus-visible):not(.Mui-focusVisible):not(:active)": {
    bgcolor: "#F0F6FF",
    backgroundColor: "#F0F6FF",
    backgroundImage: "none",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:focus:not(:active), &:focus-visible:not(:active), &.Mui-focusVisible:not(:active)": {
    outline: "none",
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_SHADOW,
  },
  "&:hover:focus:not(:active), &:hover:focus-visible:not(:active), &:hover.Mui-focusVisible:not(:active)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "2px solid #005BF0",
    color: "#005BF0",
  },
  "&:active:not(.Mui-disabled)": {
    bgcolor: "#FFFFFF !important",
    backgroundColor: "#FFFFFF !important",
    backgroundImage: "none !important",
    border: "1px solid #005BF0",
    color: "#005BF0",
    boxShadow: BTN_INSET,
  },
  "&.Mui-disabled": {
    bgcolor: "#F5F5F5 !important",
    backgroundColor: "#F5F5F5 !important",
    backgroundImage: "none !important",
    border: "1px solid #E6E6E6 !important",
    color: "#BBBBBB !important",
    boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.08)",
    opacity: "1 !important",
    cursor: "not-allowed",
  },
};

export const CARD_SX = {
  bgcolor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "12px",
};

export const SECTION_TITLE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 18,
  fontWeight: 800,
  lineHeight: "100%",
  color: "#0F172A",
};

export const SECTION_SUBTITLE_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 14,
  fontWeight: 400,
  lineHeight: "150%",
  color: "#64748B",
  mt: 0.75,
};

export const LABEL_SX = {
  fontFamily: "Lato, sans-serif",
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.4px",
  textTransform: "uppercase",
  color: "#94A3B8",
};
